import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSale, getSales, getSettings, printJob, refundSale } from '../utils/api';
import { fmt } from '../utils/format';
import { openReceiptPreview } from '../utils/receipt';
import toast from 'react-hot-toast';

function Modal({ open, onClose, title, children, footer, size = '' }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size} scale-in`}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function SalesPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [method, setMethod] = useState('');
  const [selectedSaleId, setSelectedSaleId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sales', { page, method }],
    queryFn: () => getSales({ page, limit: 30, method: method || undefined }).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: settings = {} } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getSettings().then((r) => r.data),
  });

  const { data: saleDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['sale-detail', selectedSaleId],
    queryFn: () => getSale(selectedSaleId).then((r) => r.data),
    enabled: !!selectedSaleId,
  });

  const refundMut = useMutation({
    mutationFn: (id) => refundSale(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      if (selectedSaleId) qc.invalidateQueries({ queryKey: ['sale-detail', selectedSaleId] });
      toast.success('Sale refunded');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Refund failed'),
  });

  const printMut = useMutation({
    mutationFn: (sale) => printJob('receipt', { ...sale, settingsSnapshot: settings }, navigator.userAgent.slice(0, 80)),
    onSuccess: () => toast.success('Receipt queued for printing'),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to queue receipt print'),
  });

  const sales = data?.sales || [];
  const total = data?.total || 0;
  const methodColor = { cash: 'badge-gray', mpesa: 'badge-green', card: 'badge-teal', credit: 'badge-amber' };

  const csvRows = useMemo(() => [
    ['Receipt', 'Time', 'Cashier', 'Items', 'Subtotal', 'Tax', 'Total', 'Method', 'Status'],
    ...sales.map((sale) => [
      sale.receipt_no,
      fmt.datetime(sale.created_at),
      sale.cashier_name,
      sale.item_count,
      sale.subtotal,
      sale.tax_amt,
      sale.total,
      sale.payment_method,
      sale.status,
    ]),
  ], [sales]);

  function previewReceipt(sale) {
    openReceiptPreview(sale, settings);
  }

  return (
    <div className="p-page fade-up">
      <div className="flex gap-10 mb-16">
        <select className="form-input form-select" value={method} onChange={(e) => setMethod(e.target.value)} style={{ width: 180 }}>
          <option value="">All methods</option>
          <option value="cash">Cash</option>
          <option value="mpesa">M-Pesa</option>
          <option value="card">Card</option>
          <option value="credit">Credit</option>
        </select>
        <button className="btn btn-secondary" onClick={() => downloadCsv(`sales-${new Date().toISOString().slice(0, 10)}.csv`, csvRows)}>Export CSV</button>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Sales History</div>
          <span className="badge badge-gray">{total} records</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Receipt</th><th>Time</th><th>Cashier</th><th>Items</th><th>Subtotal</th><th>Tax</th><th>Total</th><th>Method</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : sales.length === 0 ? (
                <tr><td colSpan="10"><div className="empty-state"><div className="empty-icon">[]</div><div className="empty-title">No sales found</div></div></td></tr>
              ) : sales.map((sale) => (
                <tr key={sale.id}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink3)' }}>{sale.receipt_no?.slice(-12)}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{fmt.datetime(sale.created_at)}</td>
                  <td>{sale.cashier_name}</td>
                  <td>{sale.item_count}</td>
                  <td className="td-mono">{fmt.currency(sale.subtotal)}</td>
                  <td className="td-mono text-muted">{fmt.currency(sale.tax_amt)}</td>
                  <td className="td-mono td-main">{fmt.currency(sale.total)}</td>
                  <td><span className={`badge ${methodColor[sale.payment_method] || 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{sale.payment_method}</span></td>
                  <td><span className={`badge ${sale.status === 'completed' ? 'badge-green' : sale.status === 'refunded' ? 'badge-amber' : 'badge-red'}`} style={{ textTransform: 'capitalize' }}>{sale.status}</span></td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => setSelectedSaleId(sale.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 30 && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1}>Prev</button>
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Page {page} of {Math.ceil(total / 30)}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage((value) => value + 1)} disabled={page >= Math.ceil(total / 30)}>Next</button>
          </div>
        )}
      </div>

      <Modal
        open={!!selectedSaleId}
        onClose={() => setSelectedSaleId(null)}
        title={`Sale: ${saleDetail?.receipt_no?.slice(-12) || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSelectedSaleId(null)}>Close</button>
          <button className="btn btn-secondary" onClick={() => saleDetail && previewReceipt(saleDetail)} disabled={!saleDetail}>Preview / Save PDF</button>
          <button className="btn btn-ghost" onClick={() => saleDetail && printMut.mutate(saleDetail)} disabled={!saleDetail || printMut.isPending}>Queue Print</button>
          {saleDetail?.status === 'completed' && <button className="btn btn-danger" onClick={() => { if (window.confirm('Refund this sale?')) refundMut.mutate(saleDetail.id); }} disabled={refundMut.isPending}>Refund</button>}
        </>}
      >
        {detailLoading || !saleDetail ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>
        ) : (
          <div>
            <div className="flex gap-16 mb-16" style={{ flexWrap: 'wrap' }}>
              <div><div className="form-label">Cashier</div><div style={{ fontWeight: 600 }}>{saleDetail.cashier_name}</div></div>
              <div><div className="form-label">Time</div><div style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{fmt.datetime(saleDetail.created_at)}</div></div>
              <div><div className="form-label">Payment</div><span className={`badge ${methodColor[saleDetail.payment_method] || 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{saleDetail.payment_method}</span></div>
              <div><div className="form-label">Status</div><span className={`badge ${saleDetail.status === 'completed' ? 'badge-green' : 'badge-amber'}`} style={{ textTransform: 'capitalize' }}>{saleDetail.status}</span></div>
            </div>

            <div style={{ background: 'var(--bg2)', borderRadius: 'var(--r)', padding: 16 }}>
              {(saleDetail.items || []).map((item) => (
                <div key={`${item.id}-${item.product_name}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.product_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{fmt.currency(item.price)} x {item.qty}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--mono)' }}>{fmt.currency(item.line_total)}</div>
                </div>
              ))}
              <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                <div className="flex-between"><span>Subtotal</span><span className="mono">{fmt.currency(saleDetail.subtotal)}</span></div>
                <div className="flex-between"><span>VAT</span><span className="mono">{fmt.currency(saleDetail.tax_amt)}</span></div>
                {Number(saleDetail.discount_pct || 0) > 0 && <div className="flex-between"><span>Discount</span><span className="mono">- {fmt.currency(saleDetail.discount_amt)}</span></div>}
                <div className="flex-between" style={{ fontWeight: 700 }}><span>Total</span><span className="mono">{fmt.currency(saleDetail.total)}</span></div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
